// Encryption utility for localStorage
class LocalStorageEncryption {
  constructor() {
    // Your secret key - keep this secure and unique to your app
    this.SECRET_KEY = 'My$uper$trongS3cretKey!2025#';
  }

  // Generate a key from your secret
  async generateKey() {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(this.SECRET_KEY);
    
    // Create a hash of your secret key
    const hashBuffer = await crypto.subtle.digest('SHA-256', keyData);
    
    // Import the hash as a cryptographic key
    return await crypto.subtle.importKey(
      'raw',
      hashBuffer,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // Encrypt data
  async encrypt(data) {
    try {
      const key = await this.generateKey();
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(JSON.stringify(data));
      
      // Generate a random IV (Initialization Vector)
      const iv = crypto.getRandomValues(new Uint8Array(12));
      
      // Encrypt the data
      const encryptedBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        dataBuffer
      );
      
      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedBuffer), iv.length);
      
      // Convert to base64 for storage
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  // Decrypt data
  async decrypt(encryptedData) {
    try {
      const key = await this.generateKey();
      
      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      );
      
      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      
      // Decrypt the data
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encrypted
      );
      
      // Convert back to string and parse JSON
      const decoder = new TextDecoder();
      const decryptedString = decoder.decode(decryptedBuffer);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  // Store encrypted data in localStorage
  async setItem(key, data) {
    try {
      const encrypted = await this.encrypt(data);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Error storing encrypted data:', error);
      throw error;
    }
  }

  // Get and decrypt data from localStorage
  async getItem(key) {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;
      
      return await this.decrypt(encrypted);
    } catch (error) {
      console.error('Error retrieving encrypted data:', error);
      // If decryption fails, remove the corrupted data
      localStorage.removeItem(key);
      return null;
    }
  }

  // Remove item from localStorage
  removeItem(key) {
    localStorage.removeItem(key);
  }

  // Clear all localStorage
  clear() {
    localStorage.clear();
  }
}

// Usage example
const secureStorage = new LocalStorageEncryption();

export const getUserData = async () => {
  try {
    const userData = await secureStorage.getItem("SecureSignupData");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Failed to retrieve user data:", error);
    return null;
  }
};
export const getSplashUserData = async () => {
  try {
    const userData = await secureStorage.getItem("SecureSplashData");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Failed to retrieve user data:", error);
    return null;
  }
};
// Function to get user token for API calls
export const getUserToken = async () => {
  try {
    const userData = await getUserData();
    return userData?.data?.token || null;
  } catch (error) {
    console.error("Failed to retrieve user token:", error);
    return null;
  }
};
export const getUserId = async()=>{
    try {
        const userData = await getUserData();
        return userData?.data?.id || null;
    } catch (error) {
        console.error("Failed to retrieve user ID:", error);
        return null;
    }
}
// Function to check if user is logged in
export const isUserLoggedIn = async () => {
  try {
    const userData = await getUserData();
    return userData && userData.data && userData.data.token;
  } catch (error) {
    console.error("Failed to check login status:", error);
    return false;
  }
};


// Export for use in React components
export { LocalStorageEncryption, secureStorage };